import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { PORTAL_RINGS, PORTAL_Z, ROOM_H, getDailyText } from './constants';

export default function PortalTextAlternator({ isNearPortal }) {
  // Alternation (imperative — avoids R3F re-renders)
  const timerRef       = useRef(0);
  const phaseRef       = useRef(0);      // 0=portal, 1=text
  const opPortalRef    = useRef(1.0);
  const opTextRef      = useRef(0.0);
  const scalePortalRef = useRef(1.0);
  const scaleTextRef   = useRef(0.85);
  const PORTAL_DURATION = 3.0;   // durée affichage portail
  const TEXT_DURATION   = 5.0;   // durée affichage texte (+2s)
  const FADE_SPEED      = 9.0;

  const isNearRef = useRef(isNearPortal);
  useEffect(() => { isNearRef.current = isNearPortal; }, [isNearPortal]);

  // Portal refs
  const portalGroupRef = useRef();
  const ringsRef       = useRef([]);
  const glowRef        = useRef();
  const outerGlowRef   = useRef();
  const mistGroupRef   = useRef();
  const portalTimeRef  = useRef(0);

  // Text refs
  const textGroupRef = useRef();
  const textMatRef   = useRef();

  // Mist texture
  const mistTex = useMemo(() => {
    const c = document.createElement('canvas');
    c.width = 64; c.height = 64;
    const ctx = c.getContext('2d');
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 30);
    g.addColorStop(0,    'rgba(255, 245, 200, 0.75)');
    g.addColorStop(0.45, 'rgba(240, 218, 140, 0.30)');
    g.addColorStop(1,    'rgba(220, 190, 100, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    return new THREE.CanvasTexture(c);
  }, []);

  const mistData = useMemo(() => Array.from({ length: 10 }, (_, i) => ({
    angle:  (i / 10) * Math.PI * 2,
    radius: 1.1 + (i % 3) * 0.55,
    phase:  (i / 10) * Math.PI * 2,
    spd:    0.08 + (i % 4) * 0.04,
    sz:     0.65 + (i % 3) * 0.30,
  })), []);

  const mistSprites = useMemo(() => mistData.map(d => {
    const mat    = new THREE.SpriteMaterial({ map: mistTex, transparent: true, opacity: 0, depthWrite: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(d.sz, d.sz, 1);
    return { sprite, mat, data: d };
  }), [mistData, mistTex]);

  useEffect(() => {
    const group = mistGroupRef.current;
    if (!group) return;
    mistSprites.forEach(({ sprite }) => group.add(sprite));
    return () => mistSprites.forEach(({ sprite }) => group.remove(sprite));
  }, [mistSprites]);

  // Text canvas — clair sur fond sombre, lisible
  const textTex = useMemo(() => {
    const text  = getDailyText();
    const W = 2048, H = 640;
    const cv  = document.createElement('canvas');
    cv.width  = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);

    const lines      = text.quote.split('\n');
    // ── Ajustement automatique de la taille de police ──
    // Certaines phrases françaises longues dépassent 2048px à 82px →
    // ctx.measureText garantit qu'aucune ligne n'est tronquée (desktop et mobile)
    let fs = lines.length > 1 ? 82 : 96;
    ctx.font = `italic bold ${fs}px "Cormorant Garamond", Georgia, serif`;
    const maxLineWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
    if (maxLineWidth > W * 0.86) {
      fs = Math.floor(fs * (W * 0.86) / maxLineWidth);
    }
    const lh         = fs * 1.55;
    const totalTextH = lines.length * lh;
    const quoteY     = H * 0.40 - totalTextH / 2 + fs * 0.55;

    // PASS 1 — halo très sombre, presque noir
    ctx.save();
    ctx.shadowColor   = 'rgba(5, 10, 35, 0.65)';
    ctx.shadowBlur    = 45;
    ctx.shadowOffsetY = 0;
    ctx.font          = `italic bold ${fs}px "Cormorant Garamond", Georgia, serif`;
    ctx.fillStyle     = 'rgba(5, 12, 42, 0.18)';
    ctx.textAlign     = 'center';
    lines.forEach((line, i) => ctx.fillText(line, W / 2, quoteY + i * lh));
    ctx.restore();

    // PASS 2 — bleu nuit quasi noir, élégant sur fond clair
    ctx.save();
    ctx.shadowColor   = 'rgba(8, 18, 55, 0.55)';
    ctx.shadowBlur    = 18;
    ctx.font          = `italic bold ${fs}px "Cormorant Garamond", Georgia, serif`;
    ctx.fillStyle     = 'rgba(6, 14, 48, 0.97)';
    ctx.textAlign     = 'center';
    lines.forEach((line, i) => ctx.fillText(line, W / 2, quoteY + i * lh));
    ctx.restore();

    // Ornement quasi noir
    const sepY = quoteY + totalTextH + 28;
    ctx.save();
    ctx.shadowColor = 'rgba(5, 10, 38, 0.45)';
    ctx.shadowBlur  = 10;
    ctx.fillStyle   = 'rgba(8, 16, 50, 0.65)';
    ctx.font        = '18px serif';
    ctx.textAlign   = 'center';
    ctx.fillText('· · ✦ · ·', W / 2, sepY);
    ctx.restore();

    // Phrase de fin — bleu très sombre / encre
    ctx.save();
    ctx.shadowColor = 'rgba(5, 12, 40, 0.60)';
    ctx.shadowBlur  = 22;
    ctx.font        = `italic bold 58px "Cormorant Garamond", Georgia, serif`;
    ctx.fillStyle   = 'rgba(6, 14, 48, 0.97)';
    ctx.textAlign   = 'center';
    ctx.fillText(text.end, W / 2, sepY + 64);
    ctx.restore();

    const t           = new THREE.CanvasTexture(cv);
    t.colorSpace      = THREE.SRGBColorSpace;
    return t;
  }, []);

  useFrame((_, delta) => {
    timerRef.current += delta;
    const currentDuration = phaseRef.current === 0 ? PORTAL_DURATION : TEXT_DURATION;
    if (timerRef.current >= currentDuration) {
      phaseRef.current = phaseRef.current === 0 ? 1 : 0;
      timerRef.current = 0;
    }

    const targetPortalOp    = phaseRef.current === 0 ? 1.0 : 0.0;
    const targetTextOp      = phaseRef.current === 1 ? 1.0 : 0.0;
    const targetPortalScale = phaseRef.current === 0 ? 1.0 : 0.86;
    const targetTextScale   = phaseRef.current === 1 ? 1.0 : 0.86;

    const k = Math.min(FADE_SPEED * delta, 1);
    opPortalRef.current    += (targetPortalOp    - opPortalRef.current)    * k;
    opTextRef.current      += (targetTextOp      - opTextRef.current)      * k;
    scalePortalRef.current += (targetPortalScale - scalePortalRef.current) * k;
    scaleTextRef.current   += (targetTextScale   - scaleTextRef.current)   * k;

    const sp = scalePortalRef.current;
    const st = scaleTextRef.current;
    if (portalGroupRef.current) portalGroupRef.current.scale.set(sp, sp, 1);
    if (textGroupRef.current)   textGroupRef.current.scale.set(st, st, 1);

    portalTimeRef.current += delta;
    const pt     = portalTimeRef.current;
    const pOp    = opPortalRef.current;
    const isNear = isNearRef.current;

    PORTAL_RINGS.forEach((ring, i) => {
      if (ringsRef.current[i]) {
        ringsRef.current[i].rotation.z = pt * ring.spd;
        if (ringsRef.current[i].material) {
          ringsRef.current[i].material.opacity = pOp * (isNear ? 0.82 : 0.58);
        }
      }
    });

    if (glowRef.current?.material)      glowRef.current.material.opacity      = pOp * (0.20 + Math.sin(pt * 0.9) * 0.08);
    if (outerGlowRef.current?.material) outerGlowRef.current.material.opacity  = pOp * (0.055 + Math.sin(pt * 0.55) * 0.025);

    mistSprites.forEach(({ sprite, mat, data }) => {
      const tt   = (pt * data.spd + data.phase) % (Math.PI * 2);
      const life = (Math.sin(tt - Math.PI / 2) + 1) / 2;
      const r    = data.radius * (0.85 + life * 0.25);
      sprite.position.set(Math.cos(data.angle + pt * 0.05) * r, Math.sin(data.angle + pt * 0.05) * r, 0);
      mat.opacity = pOp * (isNear ? 0.24 : 0.12) * Math.sin(life * Math.PI);
    });

    if (textMatRef.current) textMatRef.current.opacity = opTextRef.current;
  });

  // ── Ring geometry args stabilisés pour éviter re-renders inutiles ──
  const ringArgs = useMemo(
    () => PORTAL_RINGS.map(ring => [ring.inner, ring.outer, 80]),
    [] // PORTAL_RINGS est une constante du module
  );

  return (
    <>
      <group ref={portalGroupRef} position={[0, ROOM_H * 0.50, PORTAL_Z]}>
        {PORTAL_RINGS.map((ring, i) => (
          <mesh key={`ring-${i}`} ref={el => { ringsRef.current[i] = el; }}>
            <ringGeometry args={ringArgs[i]} />
            <meshBasicMaterial color={ring.color} transparent opacity={0.58} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
        ))}
        <group ref={mistGroupRef} />
        <mesh ref={glowRef}>
          <circleGeometry args={[0.58, 64]} />
          <meshBasicMaterial color="#FFF5D0" transparent opacity={0.22} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
        <mesh ref={outerGlowRef} position={[0, 0, -0.05]}>
          <circleGeometry args={[3.5, 64]} />
          <meshBasicMaterial color="#C9A84C" transparent opacity={0.06} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
        <pointLight color="#FFD78A" intensity={isNearPortal ? 22 : 12} distance={20} decay={2} />
        <pointLight color="#FFF0D0" intensity={isNearPortal ? 8 : 4} distance={12} decay={2} />
        <mesh position={[0, -ROOM_H * 0.50 + 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[4.0, 48]} />
          <meshBasicMaterial color="#C9A84C" transparent opacity={0.12} depthWrite={false} />
        </mesh>
      </group>

      <group ref={textGroupRef} position={[0, 4.0, -46]}>
        <mesh>
          <planeGeometry args={[16.5, 4.83]} />
          <meshBasicMaterial ref={textMatRef} map={textTex} transparent opacity={0} depthWrite={false} />
        </mesh>
      </group>
    </>
  );
}
